import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { WithTooltip } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions, StatusIcon } from 'primary-components';

import { basicPipelineDashboardActions } from 'actions/modules/datascience/dashboard';
import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';
import { statusesQueries } from 'queries/modules/statuses';

import { useQuery } from '@tanstack/react-query';

import './StatusRow.scss';

const EMPTY_OBJ = {};

const useSearchStatuses = ({ query, groupName, type, getQueryBuilder, shouldBeLimited, enabled }) =>
  useQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query, 
        groupName,
        type,
        aggregate: true,
        size: 1,
      }
    ),
    enabled,
    refetchInterval: () => shouldBeLimited() ? undefined : 50_000,
    getNextPageParam: CommonService.getNextPageParam,
    select: response => response.items?.[0]
  })
const StatusRow = ({ name, optional = "", type, groupName = undefined }) => {
  const dispatch = useDispatch(),
        storageScreen = useSelector(state => state.myAccountState.settingsState.storageScreen),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())).withFetching(false), [dispatch]),
        isFormula = statusesActions.isFormulaType(type),
        { data: statusItem } = useSearchStatuses({ getQueryBuilder, shouldBeLimited, query: name, groupName, type, enabled: !isFormula }),
        // A formula's status is its latest run, which only the component status search has.
        { data: latestFormulaRun } = statusesQueries.useGetLatestFormulaRun({ getQueryBuilder, groupName, name, shouldBeLimited, enabled: isFormula }),
        item = (isFormula ? latestFormulaRun : statusItem) || EMPTY_OBJ,
        status = item?.status,
        warnStatuses = item?.warnStatuses,
        message = item?.latestStatus?.message,
        jobId = item?.jobId,
        href = useMemo(() => {
          if(type === StatusesEnums.STATUS_TYPE.ETL) {
            return DashboardLinkFactory.dataLake.group({ groupName: name, query: "" })
          } else if(type === StatusesEnums.STATUS_TYPE.DATAPREP) {
            return DashboardLinkFactory.dataStorage.dataPrep({ groupName: optional, jobId, name, storageScreen })
          } else if(isFormula) {
            return basicPipelineDashboardActions.getFormulaMonitorUrl({ groupName, latestRun: latestFormulaRun, name })
          } else {
            return DashboardLinkFactory.dataScience.pipeline({ groupName: optional, jobId, pipelineName: name })
          }
        }, [groupName, isFormula, jobId, latestFormulaRun, name, optional, storageScreen, type])

  return (
    <a 
        className="search-all__content__results__recents__element__row"
        href={href}
    >
      <WithTooltip
          data-place="top"
          text={message}
      >
        <div className="search-all__content__results__recents__element__row__left">
          <StatusIcon
              status={status}
              warnStatuses={warnStatuses}
          />
        </div>
      </WithTooltip>
      <div className="search-all__content__results__recents__element__row__right">
        <div className="search-all__content__results__recents__element__row__right__top">
          {name}
        </div>
        <div className="search-all__content__results__recents__element__row__right__bottom">
          {optional}
        </div>
      </div>
    </a>
  )
}

StatusRow.propTypes = {
  groupName: PropTypes.string,
  name: PropTypes.string.isRequired,
  optional: PropTypes.string,
  type: PropTypes.string.isRequired
}

export default memo(StatusRow)
